import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Trans, t } from '@lingui/macro';
import { DataItem, Spinner } from '@toqio/toqio-web-components';
import { useBridge } from '../../../bridge/useBridge';
import { useFlowReady } from '../../../bridge/useFlowReady';
import { BackButton, EmptyState, PageContainer, PageHeader } from '../../../shared/components';
import { DetailMeta } from '../components/DetailHeader';
import { formatAmount, GREY } from '../components/styles';
import { usePaymentDetail } from '../usePaymentDetail';

const FLOW_ID = 'pay-by-bank-backoffice';

/**
 * Detail screen (PRD §2/§3/§4d): read-only consolidated detail of one payment,
 * fetched by `revenueId` from the path (deep-linkable). Emits `step:change` on
 * mount, signals readiness once the first load settles, and on the host
 * `back:request` (or the Back link) returns to the list.
 */
// Horizontal gutter comes from PageContainer (.lum-web-container); only the
// vertical padding lives here. max-width narrows the read-only detail content.
const Page = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px 0;
  max-width: 920px;
`;

/** Sections below the header: the 32px to the header is the PageHeader's own
 * baked bottom margin, so only the inter-section gap lives here. */
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font:
    600 16px/24px Inter,
    sans-serif;
  color: ${GREY};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px 60px;

  // Single column on mobile (matches the list reflow breakpoint): two columns
  // with a 60px gutter are too cramped on a phone.
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 56px 24px;
`;

export const PaymentDetailStep: React.FC = () => {
  const bridge = useBridge();
  const navigate = useNavigate();
  const { signalReady } = useFlowReady();
  const { revenueId = '' } = useParams<{ revenueId: string }>();
  const locale = bridge.locale ?? 'en';

  const { phase, data, errorCode, reload } = usePaymentDetail(revenueId);

  const goToList = React.useCallback(
    () => navigate('../../list', { relative: 'path' }),
    [navigate],
  );

  useEffect(() => {
    bridge.emit({
      type: 'step:change',
      flowId: FLOW_ID,
      stepId: 'detail',
      stepIndex: 0,
      totalSteps: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase === 'loading') return;
    signalReady();
  }, [phase, signalReady]);

  useEffect(() => bridge.onBackRequest(goToList), [bridge, goToList]);

  if (phase === 'loading') {
    return (
      <PageContainer>
        <Centered>
          <Spinner />
        </Centered>
      </PageContainer>
    );
  }

  if (phase === 'error' || !data) {
    const notFound = errorCode === 'PAYMENT_NOT_FOUND';
    const forbidden = errorCode === 'FORBIDDEN';
    return (
      <PageContainer>
        <Page>
          <BackButton onClick={goToList} label={<Trans id="pbbbo.detail.back">Back</Trans>} />
          <Centered>
            {forbidden ? (
              <EmptyState
                title={t`You don't have access to this section.`}
                description={t`Try adjusting your filters to find what you are looking for.`}
              />
            ) : notFound ? (
              <EmptyState
                title={t`Payment not found.`}
                description={t`Try adjusting your filters to find what you are looking for.`}
              />
            ) : (
              <EmptyState
                variant="error"
                description={t`We couldn't load the payments. Please try again.`}
                action={{ label: t`Try again`, onClick: reload }}
              />
            )}
          </Centered>
        </Page>
      </PageContainer>
    );
  }

  const description = data.customField2 ?? data.concept ?? '-';
  const transactionOwner = data.senderName
    ? `${data.externalClientId} / ${data.senderName}`
    : data.externalClientId;
  const detailStatus = data.detailedStatus
    ? `${data.paymentStatus}: ${data.detailedStatus}`
    : data.paymentStatus;

  return (
    <PageContainer>
      <Page>
        <PageHeader
          title={`${formatAmount(data.invoiceAmount, locale)} ${data.invoiceCurrency}`}
          showBackButton
          onClickBack={goToList}
          backLabel={<Trans id="pbbbo.detail.back">Back</Trans>}
          testId="pbbbo-detail-header"
          info={<DetailMeta payment={data} locale={locale} />}
        />

        <Body>
          <Section>
            <SectionTitle>
              <Trans>Outbound payment details</Trans>
            </SectionTitle>
            <Grid>
              <DataItem label={t`Transaction owner`} value={transactionOwner} />
              <DataItem label={t`Description`} value={description} />
              <DataItem label={t`IBAN`} value={data.senderIban ?? '-'} />
              {data.bankPaymentScheme && (
                <DataItem label={t`Payment scheme`} value={data.bankPaymentScheme} />
              )}
              {data.customField1 && <DataItem label={t`Payment type`} value={data.customField1} />}
              {data.concept && <DataItem label={t`Bank reference`} value={data.concept} />}
            </Grid>
          </Section>

          <Section>
            <SectionTitle>
              <Trans>Payment destination</Trans>
            </SectionTitle>
            <Grid>
              <DataItem label={t`IBAN`} value={data.receiverIban ?? '-'} />
              {data.receiverName && <DataItem label={t`Beneficiary`} value={data.receiverName} />}
              <DataItem label={t`Status`} value={detailStatus} />
            </Grid>
          </Section>
        </Body>
      </Page>
    </PageContainer>
  );
};
